import { defaultDataIdFromObject, InMemoryCache } from '@apollo/client'
import { merge, mergeWith, unionBy, uniqBy } from 'lodash-es'

import { possibleTypes } from './consts'

export const cache = new InMemoryCache({
  dataIdFromObject(responseObject) {
    if (
      responseObject.__typename === 'SavedPostSearch' ||
      responseObject.__typename === 'Topic' ||
      responseObject.__typename === 'Subtopic' ||
      responseObject.__typename === 'Niche' ||
      responseObject.__typename === 'IndustryTopic' ||
      responseObject.__typename === 'BrandTerritory' ||
      responseObject.__typename === 'TrendingSubject' ||
      responseObject.__typename === 'BrandGroup' ||
      responseObject.__typename === 'TerritoryTag' ||
      responseObject.__typename === 'GlobalTopic' ||
      responseObject.__typename === 'HotTopic' ||
      responseObject.__typename === 'TaxonomyTheme' ||
      responseObject.__typename === 'TaxonomySegment' ||
      responseObject.__typename === 'TaxonomySubject' ||
      responseObject.__typename === 'TaxonomyEntityQuery' ||
      responseObject.__typename === 'TaxonomyEntity'
    ) {
      if (responseObject.territoryId) {
        return `Territory:${responseObject.territoryId}`
      }

      if (responseObject.__typename === 'SavedPostSearch' && responseObject.id) {
        return `Territory:saved_search.${responseObject.id}`
      }

      if (responseObject.__typename === 'Topic') {
        if (responseObject.topicId || responseObject.id) {
          return `Territory:topic.${responseObject.topicId || responseObject.id}`
        }
      }

      if (responseObject.__typename === 'Subtopic') {
        if (responseObject.subtopicId || responseObject.id) {
          return `Territory:subtopic.${responseObject.subtopicId || responseObject.id}`
        }
      }
    }

    return defaultDataIdFromObject(responseObject)
  },
  possibleTypes,
  typePolicies: {
    Query: {
      fields: {
        listTrendingSubjectPaginated: {
          keyArgs: ['filters'],
          merge(existing, incoming, { args }) {
            if (!existing) return incoming

            const existingData = existing.data || []
            const incomingData = incoming.data || []

            return {
              ...incoming,
              data: args?.pagination?.cursor ? [...existingData, ...incomingData] : incomingData,
            }
          },
          read(existing) {
            return existing
          },
        },
        getJobDashboardComponentById: {
          keyArgs: ['componentId', 'dashboardId'],
          merge(existing, incoming) {
            return incoming
          },
        },
        listSubtopics: {
          keyArgs: ['language', 'query', 'sort', 'text', 'isTrending', 'topicIds'],
          merge(existing, incoming, { args, readField }) {
            //if offset is 0, clear cache
            if (existing?.entities?.length && args.offset !== 0) {
              const existingIdSet = new Set(existing?.entities?.map(subtopic => readField('subtopicId', subtopic)))

              const incomingCopy = incoming.entities.filter(
                subtopic => !existingIdSet.has(readField('subtopicId', subtopic))
              )

              return {
                ...existing,
                entities: [...(existing?.entities || []), ...incomingCopy],
              }
            } else {
              return {
                ...incoming,
              }
            }
          },
        },
        territoryTrending: {
          merge: true,
        },
        listSavedPostSearches: {
          merge(existing, incoming) {
            return [...incoming]
          },
        },
        getTerritory: {
          keyArgs: ['territoryId'],
          merge(existing, incoming, { isReference }) {
            if (!existing) return incoming

            if (isReference(incoming) || isReference(existing)) return incoming

            const incomingTerritoryMostEngaged = incoming.territoryMostEngaged || {}
            const existingTerritoryMostEngaged = existing.territoryMostEngaged || {}
            const territoryMostEngaged = merge({}, existingTerritoryMostEngaged, incomingTerritoryMostEngaged)

            const existingPosts = existingTerritoryMostEngaged.posts || []
            const incomingPosts = territoryMostEngaged.posts || []

            const posts = uniqBy([...existingPosts, ...incomingPosts], 'postId')

            return {
              ...incoming,
              territoryMostEngaged: {
                ...territoryMostEngaged,
                posts,
              },
            }
          },
          read(existing, { args, toReference }) {
            return (
              existing ||
              toReference({
                __typename: 'SavedPostSearch',
                territoryId: args.territoryId,
              })
            )
          },
        },
      },
    },
    BrandTerritory: {
      fields: {
        territoryPerformance: {
          merge(existing, incoming) {
            return merge({}, existing, incoming)
          },
        },
        territoryRelated: {
          merge: true,
        },
        territoryAlsoWatch: {
          merge: true,
        },
        territoryAudience: {
          merge: true,
        },
        territoryPosts: {
          merge: true,
        },
        // No merge means each write replaces the whole field, dropping the storage slot the
        // default per-args keying (clusters(filters: $filters)) built for the previous date
        // filter — same class of issue territoryPosts/territoryPerformance already solve above.
        territoryCreators: {
          merge: true,
        },
        territoryMostEngaged: {
          merge: true,
        },
        territorySentiment: {
          merge: true,
        },
      },
    },
    WorkspaceBrand: {
      fields: {
        workspaceBrandTerritories: {
          merge(existing, incoming) {
            return merge({}, existing, incoming)
          },
        },
      },
    },
    // workspaceBrandTerritoriesV2 has no id, and different queries (portfolio-tracking's
    // volume/virality/velocity/variety) each select a different subset of its owned/earned/paid
    // territories. Without a merge function, Apollo replaces the whole object on every write and
    // drops whatever the sibling queries already cached there — same issue workspaceBrandTerritories
    // (V1, above) already had to solve.
    WorkspaceBrandV2: {
      fields: {
        workspaceBrandTerritoriesV2: {
          merge(existing, incoming) {
            return merge({}, existing, incoming)
          },
        },
      },
    },
    Subtopic: {
      fields: {
        topic: {
          merge: true,
        },
        territoryAlsoWatch: {
          merge: true,
        },
        territoryPerformance: {
          keyArgs: false,
          merge(existing = {}, incoming) {
            return {
              ...existing,
              ...incoming,
            }
          },
        },
      },
    },
    Niche: {
      keyFields: ['territoryId'],
      fields: {
        territoryAlsoWatch: {
          merge: true,
        },
        territoryPerformance: {
          keyArgs: false,
          merge(existing = {}, incoming) {
            return {
              ...existing,
              ...incoming,
            }
          },
        },
      },
    },
    GetJobDashboardComponentResponse: {
      keyFields: false,
    },
    // These clusters carry a stable `id`, but the portfolio-tracking variety chart's
    // mega/macro/mid/micro/nano creator counts are scoped to the date filter passed as
    // `clusters(filters: $filters)` — normalizing by id shares that data across every
    // date range that happens to return the same cluster id, so switching filters clobbers
    // the previous range's counts instead of caching them separately.
    CreatorClusterBrands: {
      keyFields: false,
    },
    CreatorClusterEngagement: {
      keyFields: false,
    },
    CreatorClusterSubscriber: {
      keyFields: false,
    },
    CreatorClusterSubtopic: {
      keyFields: false,
    },
    CreatorClusterTopic: {
      keyFields: false,
    },
    CreatorClusterTrending: {
      keyFields: false,
    },
    TerritoryCompareComponent: {
      keyFields: ['id'],
      fields: {
        data: {
          merge(_, incoming) {
            return incoming
          },
        },
      },
    },
    TerritoryPerformanceMetrics: {
      keyFields: false,
      merge(existing = {}, incoming) {
        return {
          ...existing,
          ...incoming,
        }
      },
    },
    TerritoryPerformanceSampledMetrics: {
      keyFields: false,
    },
    WorkspaceJob: {
      fields: {
        pins: {
          merge: true,
        },
      },
    },
    PostLiveStreaming: {
      merge: true,
    },
    // Plan has no `id` in any selection, so it is never normalized and lives inline under
    // Company.plan. getMe and getWorkspaceCompanyPlan select different Plan fields, so the default
    // (replace-on-write) clobbers whichever wrote first, leaving its watcher's cache incomplete and
    // triggering a refetch storm until the data settles. Merge field-by-field instead of replacing.
    Plan: {
      keyFields: false,
      merge: true,
    },
    TerritoryPerformance: {
      merge: true,
      fields: {
        relevance: {
          merge: true,
        },
      },
    },
    Topic: {
      fields: {
        category: {
          merge: true,
        },
        territoryAlsoWatch: {
          merge: true,
        },
        territoryPerformance: {
          keyArgs: false,
          merge(existing = {}, incoming) {
            return {
              ...existing,
              ...incoming,
            }
          },
        },
      },
    },
    BrandCompareComponent: {
      fields: {
        data: {
          merge(existing, incoming) {
            return mergeWith({}, existing, incoming, (objVal, srcVal) => {
              if (Array.isArray(objVal) && Array.isArray(srcVal)) {
                return unionBy(srcVal, objVal, 'territoryId')
              }
            })
          },
        },
      },
    },
    TerritoryTag: {
      merge(existing, incoming) {
        return merge({}, existing, incoming)
      },
      fields: {
        territoryPosts: {
          keyArgs: ['filters'],
          merge: true,
        },
        territoryAudience: {
          keyArgs: ['filters'],
          merge: true,
        },
        territoryRelated: {
          keyArgs: ['filters', 'order'],
          merge: true,
        },
        territoryAlsoWatch: {
          keyArgs: ['categoryIds', 'languages', 'pagination', 'providers', 'timeRange'],
          merge: true,
        },
        territoryCreators: {
          keyArgs: ['filters'],
          merge: true,
        },
      },
    },
    ShareOfAttentionV2: {
      fields: {
        data: {
          keyArgs: ['input', 'shareOfAttentionInput'],
          merge(existing, incoming) {
            return merge({}, existing, incoming)
          },
        },
      },
    },
    ShareOfAttentionV3: {
      keyFields: false,
      merge(existing = {}, incoming) {
        return { ...existing, ...incoming }
      },
      fields: {
        metrics: {
          keyArgs: ['input'],
          merge(existing, incoming) {
            return merge({}, existing, incoming)
          },
        },
      },
    },
  },
})
